import { Component, OnInit, ViewChild, AfterViewInit, isDevMode } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { CentreService } from 'src/app/services/centre.service';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {endOfDay} from '../../../../utils/date-range-format.service';

/**
 * Component for displaying highlighted education sessions.
 * Shows three separate tables:
 * 1. Children aged 4-14 years
 * 2. Children aged 14-18 years
 * 3. Follow-up sessions
 * All tables filter by highlight:true
 */
@Component({
  selector: 'app-education-hightlight-report',
  templateUrl: './education-hightlight-report.component.html',
  styleUrls: ['./education-hightlight-report.component.scss']
})
export class EducationHightlightReportComponent implements OnInit, AfterViewInit {
  @ViewChild('paginator1') paginator1: MatPaginator | null = null;
  @ViewChild('paginator2') paginator2: MatPaginator | null = null;
  @ViewChild('paginator3') paginator3: MatPaginator | null = null;
  @ViewChild('sort1') sort1: MatSort | null = null;
  @ViewChild('sort2') sort2: MatSort | null = null;
  @ViewChild('sort3') sort3: MatSort | null = null;

  // Form controls
  centreControl = new FormControl('All');
  startDateControl = new FormControl('');
  endDateControl = new FormControl('');

  // Table configuration
  displayedColumns: string[] = ['childName', 'sessionType', 'date', 'topic', 'submittedBy'];

  // Data sources for three tables
  dataSource4to14 = new MatTableDataSource<any>([]);
  dataSource14to18 = new MatTableDataSource<any>([]);
  dataSourceFollowUp = new MatTableDataSource<any>([]);

  // Data
  centres$: Observable<any[]> | undefined;
  loading = false;
  env = isDevMode() ? environment.testMode : environment.productionMode;

  constructor(
    private readonly afs: AngularFirestore,
    private readonly centreService: CentreService
  ) { }

  ngOnInit(): void {
    // Load centres for dropdown
    this.centres$ = this.centreService.getAllCentresOptions() as Observable<any[]>;
  }

  ngAfterViewInit(): void {
    // Wire up paginators and sorters
    if (this.paginator1) this.dataSource4to14.paginator = this.paginator1;
    if (this.paginator2) this.dataSource14to18.paginator = this.paginator2;
    if (this.paginator3) this.dataSourceFollowUp.paginator = this.paginator3;

    if (this.sort1) this.dataSource4to14.sort = this.sort1;
    if (this.sort2) this.dataSource14to18.sort = this.sort2;
    if (this.sort3) this.dataSourceFollowUp.sort = this.sort3;
  }

  /**
   * Convert form control value to Date object
   */
  private toDate(value: any): Date | undefined {
    if (!value) return undefined;
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return undefined;
      return d;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Calculate age from date of birth
   */
  private calculateAge(dob: any): number {
    if (!dob) return 0;
    try {
      const birthDate = dob.toDate ? dob.toDate() : new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch (e) {
      return 0;
    }
  }

  /**
   * Search for highlighted sessions based on selected centre and date range
   */
  search() {
    this.loading = true;
    const startDate = this.toDate(this.startDateControl.value);
    const endDate = this.toDate(this.endDateControl.value);
    const selectedCentre = this.centreControl.value || 'All';

    const educationPath = `${this.env.education}`;

    // Build query for highlighted sessions
    let query = this.afs.collection<any>(educationPath, ref => {
      let r: any = ref.where('isHighlight', '==', true);

      if (selectedCentre !== 'All') {
        r = r.where('centreCode', '==', selectedCentre);
      }

      if (startDate) {
        r = r.where('sessionDate', '>=', startDate);
      }

      if (endDate) {
        r = r.where('sessionDate', '<=', endOfDay(endDate));
      }

      return r.orderBy('date', 'desc');
    });

    query.get().subscribe({
      next: (snapshot) => {
        const sessions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Separate sessions by age groups
        const sessions4to14: any[] = [];
        const sessions14to18: any[] = [];
        const sessionsFollowUp: any[] = [];

        sessions.forEach(session => {
          // Check if it's a follow-up session
          if (session.type && session.type.toLowerCase().includes('follow-up')) {
            sessionsFollowUp.push(session);
          } else if (session.childAge !== undefined) {
            // Use childAge if available
            if (session.childAge >= 4 && session.childAge < 14) {
              sessions4to14.push(session);
            } else if (session.childAge >= 14 && session.childAge <= 18) {
              sessions14to18.push(session);
            }
          } else if (session.dateOfBirth) {
            // Calculate age from DOB
            const age = this.calculateAge(session.dateOfBirth);
            if (age >= 4 && age < 14) {
              sessions4to14.push(session);
            } else if (age >= 14 && age <= 18) {
              sessions14to18.push(session);
            }
          } else {
            // If no age info, default to 4-14 group
            sessions4to14.push(session);
          }
        });

        this.dataSource4to14.data = sessions4to14;
        this.dataSource14to18.data = sessions14to18;
        this.dataSourceFollowUp.data = sessionsFollowUp;

        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching highlighted education sessions:', err);
        this.loading = false;
      }
    });
  }

  /**
   * Apply filter to table 1 (4-14 years)
   */
  applyFilter1(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource4to14.filter = filterValue.trim().toLowerCase();
    if (this.dataSource4to14.paginator) {
      this.dataSource4to14.paginator.firstPage();
    }
  }

  /**
   * Apply filter to table 2 (14-18 years)
   */
  applyFilter2(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource14to18.filter = filterValue.trim().toLowerCase();
    if (this.dataSource14to18.paginator) {
      this.dataSource14to18.paginator.firstPage();
    }
  }

  /**
   * Apply filter to table 3 (Follow-up)
   */
  applyFilter3(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSourceFollowUp.filter = filterValue.trim().toLowerCase();
    if (this.dataSourceFollowUp.paginator) {
      this.dataSourceFollowUp.paginator.firstPage();
    }
  }

  /**
   * Clear all filters and reset the form
   */
  clearFilters() {
    this.centreControl.setValue('All');
    this.startDateControl.setValue('');
    this.endDateControl.setValue('');
    this.dataSource4to14.data = [];
    this.dataSource14to18.data = [];
    this.dataSourceFollowUp.data = [];
  }

  /**
   * Format date for display
   */
  formatDate(date: any): string {
    if (!date) return 'N/A';
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString('en-GB');
    } catch (e) {
      return 'Invalid Date';
    }
  }
}
